import { useState, useEffect, useCallback, useMemo } from 'react';

/**
 * Hook agnóstico de carrinho — gere fetch/add/update/remove/clear + totais.
 * As 2 callsites (MY checkout + CRM Nova Encomenda) injectam adapters para
 * os seus endpoints.
 *
 * @param {object} cfg
 * @param {() => Promise<Array>} cfg.fetchCart
 * @param {(id, qty) => Promise<void>} cfg.addItem
 * @param {(rowId, qty) => Promise<void>} cfg.updateQty
 * @param {(rowId) => Promise<void>} cfg.removeItem
 * @param {() => Promise<void>} [cfg.clearCart] — opcional; se ausente usa
 *   `removeItem` em loop.
 * @param {string} [cfg.rowIdField] nome do campo que identifica a linha do
 *   carrinho (default `ID_Carrinho`).
 * @param {(item) => number} [cfg.priceOf] selector para preço unitário; default
 *   `PrecoUnitario ?? PrecoBaseConsultor`.
 * @param {(item) => number} [cfg.netPointsOf] selector para netpoints
 *   (default `NetPoints`).
 */
export default function useCartLogic({
  fetchCart, addItem, updateQty, removeItem, clearCart,
  rowIdField = 'ID_Carrinho',
  priceOf = (i) => Number(i.PrecoUnitario ?? i.PrecoBaseConsultor) || 0,
  netPointsOf = (i) => Number(i.NetPoints) || 0,
} = {}) {
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = (await fetchCart()) || [];
      setCart(list);
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.warn('useCartLogic.load failed', e);
    } finally {
      setLoading(false);
    }
  }, [fetchCart]);

  useEffect(() => { load(); }, [load]);

  const add = useCallback(async (id, qty) => {
    if (Number(qty) < 1) return;
    await addItem(id, Number(qty));
    await load();
  }, [addItem, load]);

  const update = useCallback(async (rowId, qty) => {
    if (Number(qty) < 1) return;
    await updateQty(rowId, Number(qty));
    await load();
  }, [updateQty, load]);

  const remove = useCallback(async (rowId) => {
    await removeItem(rowId);
    await load();
  }, [removeItem, load]);

  const clearAll = useCallback(async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Limpar todo o carrinho?')) return;
    if (typeof clearCart === 'function') {
      await clearCart();
    } else {
      // Sequencial para evitar races no backend legacy.
      for (const item of cart) {
        // eslint-disable-next-line no-await-in-loop
        await removeItem(item[rowIdField]);
      }
    }
    await load();
  }, [cart, clearCart, removeItem, rowIdField, load]);

  const total = useMemo(
    () => cart.reduce((s, c) => s + priceOf(c) * Number(c.Quantidade || 0), 0),
    [cart, priceOf],
  );
  const netPoints = useMemo(
    () => cart.reduce((s, c) => s + netPointsOf(c) * Number(c.Quantidade || 0), 0),
    [cart, netPointsOf],
  );

  return {
    cart, loading, load,
    add, updateQty: update, remove, clearAll,
    total, netPoints,
  };
}
